import { ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	useSidebar
} from "@/components/ui/sidebar";
import { Link } from "@tanstack/react-router";

import type { SidebarItem } from "./app-sidebar";

import { Separator } from "../ui/separator";

export function NavMain({ items }: { items: SidebarItem[][] }) {
	const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});

	const { isMobile, toggleSidebar, notifications } = useSidebar();

	const toggleGroup = (title: string) => {
		setOpenGroups(prev => ({
			...prev,
			[title]: !prev[title]
		}));
	};

	return (
		<>
			{items.map((itemGroup, idx) => (
				<Fragment key={crypto.randomUUID()}>
					{idx > 0 && (
						<Separator orientation="horizontal" className="mr-2 h-[2px] opacity-10" />
					)}
					<SidebarGroup>
						<SidebarMenu className="space-y-2">
							{itemGroup.map((item) => {
								let notificationCount = 0;
								if (item.hasPushNotifications) {
									let path: string | undefined;
									if (typeof item.url === "string") {
										path = item.url.split("/").pop();
									}
									if (path && path in notifications) {
										notificationCount = notifications[path as keyof typeof notifications];
									}
								}
								return (
									<Collapsible
										key={item.title}
										open={openGroups[item?.title] || false}
										onOpenChange={() => toggleGroup(item?.title)}
										className="group/collapsible"
									>
										<SidebarMenuItem>
											<CollapsibleTrigger asChild>
												<div>
													<Link to={typeof item.url === "string" ? item.url : undefined}>
														{({ isActive }) => (
															<SidebarMenuButton
																tooltip={item.title}
																isActive={isActive}
																className="!py-6 gap-3"
																onClick={() => {
																	toggleGroup(item.title);
																	isMobile && toggleSidebar();
																}}
															>
																{item.icon && <item.icon className="!size-5" />}
																<span className="text-base font-light">{item.title}</span>
																{item.hasPushNotifications && notificationCount > 0 && (
																	<span className="ml-auto text-xs text-white bg-red-600 h-fit w-fit rounded-full px-1">
																		{notificationCount}
																	</span>
																)}
																{item?.items?.length && (
																	<ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
																)}
															</SidebarMenuButton>
														)}
													</Link>
												</div>
											</CollapsibleTrigger>
											{item?.items?.length && (
												<CollapsibleContent>
													<SidebarMenuSub>
														{item.items?.map(subItem => (
															<SidebarMenuSubItem key={subItem.title}>
																<Link to={typeof subItem.url === "string" ? subItem.url : undefined}>
																	{({ isActive }) => (
																		<SidebarMenuSubButton
																			isActive={isActive}
																			className={isActive ? "bg-white/10" : ""}
																			asChild
																		>
																			<div>
																				<span className="text-base font-light">{subItem.title}</span>
																			</div>
																		</SidebarMenuSubButton>
																	)}
																</Link>
															</SidebarMenuSubItem>
														))}
													</SidebarMenuSub>
												</CollapsibleContent>
											)}
										</SidebarMenuItem>
									</Collapsible>
								);
							})}
						</SidebarMenu>
					</SidebarGroup>
				</Fragment>
			))}
		</>
	);
}
